import { Languages } from '../../constants/constants';
import { DataFieldWithDataType } from '../types';
import { MetricCategoryDetails, MetricGroupDetails } from './metrics/types';
import { MetricCategoryTree, MetricTree } from './types-graphql';

export const cleanupMetricTree = (
  metricTreeFromBackend: MetricTree
): MetricTree => {
  // This function will remove any items that don't have ids in graphql-sdk.ts
  // This will happen if there is new data on the backend. Since frontend might not
  // support that fully(in translation, data validation in zod, etc), we just remove them from
  // the fetched list
  const cleanedMetricTree: MetricTree = {
    ...metricTreeFromBackend,
    categoryTrees: metricTreeFromBackend.categoryTrees
      .map((cTree) => {
        return {
          ...cTree,
          groupTrees: cTree.groupTrees
            .map((gTree) => {
              return {
                ...gTree,
                metricDefs: gTree.metricDefs
                  .map((mDef) => {
                    return mDef;
                  })
                  .filter(Boolean),
              };
            })
            .filter(Boolean),
        };
      })
      .filter(Boolean) as MetricCategoryTree[],
  };
  return cleanedMetricTree;
};

export const convertMetricTreeToMetricDetails = (
  metricTree: MetricTree
): {
  groupDetails: MetricGroupDetails[];
  categoryDetails: MetricCategoryDetails[];
} => {
  const metricGroupDetails = metricTree.categoryTrees.flatMap((catTree) => {
    const { metricCategoryId, groupTrees, metricCategory } = catTree;
    const metricsWithDimensions: MetricGroupDetails[] = groupTrees.map(
      (group) => {
        const { metricGroupId, metricDefs } = group;
        const metricDetails = metricDefs.map((def) => {
          return {
            id: def.id,
            metricName: {
              [Languages.EN]: def.nameEnglish,
              [Languages.JA]: def.nameJapanese,
            },
            metricType: def.metricType,
            metricGroupId: def.metricGroupId,
            formatType: def.formatType.id,
            underlyingFields:
              def.underlyingDataFields as unknown as DataFieldWithDataType[],
            defaultPosition: def.defaultPosition,
            segmentationPercAllowed: def.segmentationPercAllowed,
            lifecycleStage: def.lifecycleStage,
            tooltipJapanese: def.tooltipJapanese,
            tooltipEnglish: def.tooltipEnglish,
          };
        });
        return {
          id: metricGroupId,
          metricGroupName: {
            [Languages.EN]: group.metricGroup.nameEnglish,
            [Languages.JA]: group.metricGroup.nameJapanese,
          },
          defaultCategory: metricCategoryId,
          dimensions: metricDetails,
          defaultPosition: group.metricGroup.defaultPosition,
          defaultCategoryPosition: metricCategory.defaultPosition,
        };
      }
    );
    return metricsWithDimensions;
  });
  const metricCategoryDetails = metricTree.categoryTrees.map((catTree) => {
    const { id, nameEnglish, nameJapanese, defaultPosition } =
      catTree.metricCategory;
    return {
      id,
      metricCategoryName: {
        [Languages.EN]: nameEnglish,
        [Languages.JA]: nameJapanese,
      },
      defaultPosition,
    };
  });
  return {
    groupDetails: metricGroupDetails,
    categoryDetails: metricCategoryDetails,
  };
};
